import { Autocomplete, Badge, Box, Checkbox, Grid, Stack } from '@mui/joy';
import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { sortBy } from 'lodash';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

import CaseRelationPropTypes from '@/shared/prop-types/CaseRelationPropTypes';
import { CrmOptionType } from '~/common/enums';
import Language from '@/shared/enums/Language';
import { trpc } from '@/config/trpc';

import { getAddressString } from '@/shared/utils/helpers';
import { getInitialPhoneNumbers } from '@/shared/components/2.0/input/FormikMultiPhoneNumbersInput/FormikMultiPhoneNumbers.utils';

import useEvent from '@/shared/hooks/useEvent';
import useGetEnums from '@/queries/enums/useGetEnums';
import useGetSimpleBuildingRelations from '@/queries/buildings/useGetSimpleBuildingRelations';
import { useSearchFilter } from '@/shared/hooks/UseFilters';
import useToggle from '@/shared/hooks/UseToggle';

import BuildingContactsSuggestions from '@/containers/Cases/BuildingContactsSuggestions';
import FormikAddressForm from '@/shared/formik/FormikAddressForm';
import FormikAutocomplete from '@/shared/components/2.0/formik/FormikAutocomplete';
import FormikCheckbox from '@/shared/components/2.0/formik/FormikCheckbox';
import FormikCrmSelectorJoy from '@/shared/components/2.0/formik/FormikCrmSelectorJoy';
import FormikEmailInput from '@/shared/components/2.0/formik/FormikEmailAddressSelector';
import FormikEnumSelector from '@/shared/components/2.0/formik/FormikEnumAutocomplete';
import FormikIbansInput from '@/shared/components/2.0/input/FormikIbansInput';
import FormikMultiPhoneNumbersInput from '@/shared/components/2.0/input/FormikMultiPhoneNumbersInput/FormikMultiPhoneNumbersInput';
import FormikTextField from '@/shared/components/2.0/formik/FormikTextField';
import FormikVatInput from '@/shared/formik/FormikVatInput';

const propTypes = {
  askForClient: PropTypes.bool,
  buildingId: PropTypes.string,
  caseRelationToUpdate: CaseRelationPropTypes,
  currentCaseRelations: PropTypes.arrayOf(CaseRelationPropTypes),
  editMode: PropTypes.bool,
  minified: PropTypes.bool,
};

const emptyRelationFields = {
  addressLine: '',
  city: '',
  companyId: null,
  companyName: '',
  contactId: null,
  country: null,
  emailAddresses: [],
  firstName: '',
  gender: null,
  ibans: [],
  involvementType: null,
  isClient: false,
  isRequestor: false,
  language: null,
  lastName: '',
  phoneNumbers: [],
  postalCode: '',
  pushCrmChanges: false,
  role: null,
  subordinate: null,
  teamId: null,
  vatnumber: '',
};

const AddCaseRelationFormBody = ({
  buildingId = null,
  currentCaseRelations = null,
  editMode = false,
  caseRelationToUpdate = [],
  askForClient = false,
  minified = false,
}) => {
  const [selectedCrmInfo, setSelectedCrmInfo] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  const { setFieldValue, resetForm, initialValues } = useFormikContext();
  const { t } = useTranslation(['common', 'errors']);

  // resetForm() re-baselines Formik's initialValues to whatever we just passed it, so depending on
  // `initialValues`/`resetForm` here would recreate this callback every time it runs, retriggering
  // the prefill effects below in an infinite loop. useEvent keeps the reference stable instead.
  const applyRelationValues = useEvent((overrides) => {
    resetForm({
      values: {
        ...initialValues,
        ...emptyRelationFields,
        ...overrides,
      },
    });
  });

  const { data: contact } = trpc.contact.byId.useQuery(selectedContactId, {
    enabled: !!selectedContactId && selectedType === CrmOptionType.Contact,
  });
  const { data: company } = trpc.company.byId.useQuery(selectedCompanyId, {
    enabled:
      !!selectedCompanyId &&
      (selectedType === CrmOptionType.Company || selectedType === CrmOptionType.Team),
  });

  const buildingContactsToggle = useToggle();

  const { relations: buildingRelations } = useGetSimpleBuildingRelations(buildingId);

  const { debounceSearch, search } = useSearchFilter({ defaultSearch: '', startQueryLength: 0 });

  const otherCaseRelations = useMemo(() => {
    if (caseRelationToUpdate?.id == null) {
      return [];
    }
    return currentCaseRelations.filter((rel) => rel.id !== caseRelationToUpdate.id);
  }, [currentCaseRelations, caseRelationToUpdate]);

  const relationIdsToExclude = useMemo(
    () =>
      currentCaseRelations
        .flatMap((rel) => [rel.contactId, rel.companyId, rel.teamId])
        .filter((id) => id),
    [currentCaseRelations],
  );

  useEffect(() => {
    if (!editMode || !caseRelationToUpdate) {
      return;
    }
    setSelectedCrmInfo({
      type: caseRelationToUpdate.relationType,
    });
  }, [editMode, caseRelationToUpdate]);

  const prefillContactData = useCallback(
    (contactInfo, crmOption) => {
      applyRelationValues({
        addressLine: contactInfo?.address?.addressLine ?? '',
        city: contactInfo?.address?.city ?? '',
        companyName: contactInfo.companyName ?? '',
        contactId: contactInfo?.id ?? null,
        country: contactInfo?.address?.country ?? null,
        crmSelector: crmOption,
        emailAddresses: contactInfo?.emailAddresses?.map((address) => address.email) ?? [],
        firstName: contactInfo.firstName ?? '',
        gender: contactInfo?.gender ?? null,
        ibans: sortBy(
          contactInfo?.ibans?.map((i) => ({ ...i, tempKey: uuid() })),
          (iban) => !iban.isDefault,
        ),
        language: contactInfo?.language ?? Language.Dutch,
        lastName: contactInfo?.lastName ?? '',
        phoneNumbers: getInitialPhoneNumbers(contactInfo?.phoneNumbers) ?? [],
        postalCode: contactInfo?.address?.postalCode ?? '',
        role: contactInfo?.defaultCaseRole ?? null,
      });
    },
    [applyRelationValues],
  );

  const [companyAddresses, setCompanyAddresses] = useState();

  const prefillCompanyData = useCallback(
    (companyInfo, crmOption, hasTeam = false) => {
      applyRelationValues({
        addressLine: companyInfo?.companyAddresses?.[0]?.address?.addressLine ?? '',
        city: companyInfo?.companyAddresses?.[0]?.address?.city ?? '',
        companyId: hasTeam ? null : companyInfo?.companyId ?? companyInfo.id,
        companyName:
          companyInfo?.displayName ||
          companyInfo.companyName ||
          crmOption?.data?.displayName ||
          crmOption?.data?.companyName ||
          '',
        country: companyInfo?.companyAddresses?.[0]?.address?.country ?? null,
        crmSelector: crmOption,
        emailAddresses: companyInfo?.emailAddresses?.map((address) => address.email),
        ibans: sortBy(
          companyInfo?.ibans?.map((i) => ({ ...i, tempKey: uuid() })),
          (iban) => !iban.isDefault,
        ),
        language: companyInfo?.language ?? Language.Dutch,
        phoneNumbers: getInitialPhoneNumbers(companyInfo?.phoneNumbers) ?? [],
        postalCode: companyInfo?.companyAddresses?.[0]?.address?.postalCode ?? '',
        role: companyInfo?.defaultCaseRole,
        teamId: hasTeam ? crmOption?.value : null,
        vatnumber: companyInfo?.vatNumber ?? '',
      });
    },
    [applyRelationValues],
  );

  const reset = useCallback(() => {
    applyRelationValues({ crmSelector: null });
    setCompanyAddresses(null);
    setSelectedCrmInfo(null);
    setSelectedType(null);
    setSelectedContactId(null);
    setSelectedCompanyId(null);
  }, [applyRelationValues]);

  useEffect(() => {
    if (selectedType !== CrmOptionType.Contact || !contact || contact.id !== selectedContactId) {
      return;
    }

    prefillContactData(contact, selectedCrmInfo);
  }, [contact, prefillContactData, selectedContactId, selectedCrmInfo, selectedType]);

  useEffect(() => {
    if (
      (selectedType !== CrmOptionType.Company && selectedType !== CrmOptionType.Team) ||
      !company ||
      company.id !== selectedCompanyId
    ) {
      return;
    }

    prefillCompanyData(company, selectedCrmInfo, selectedType === CrmOptionType.Team);
  }, [company, prefillCompanyData, selectedCompanyId, selectedCrmInfo, selectedType]);

  const checkCrmSelection = async (crmOption) => {
    setCompanyAddresses(null);
    setSelectedCrmInfo(crmOption);
    setSelectedType(null);
    setSelectedContactId(null);
    setSelectedCompanyId(null);
    applyRelationValues({ crmSelector: crmOption });

    if (!crmOption) {
      return;
    }

    const { value, type, data } = crmOption;
    setSelectedType(type);

    switch (type) {
      case CrmOptionType.Contact:
        setSelectedContactId(value);
        break;
      case CrmOptionType.Company:
        setSelectedCompanyId(value);
        break;
      case CrmOptionType.Team:
        setSelectedCompanyId(data.companyId);
        break;
      default:
        break;
    }
  };

  const addressFormKey = selectedContactId ?? selectedCompanyId ?? 'empty';

  const {
    enums: { languages, genders, caseInvolvementTypes, caseRoles },
  } = useGetEnums();

  const prefillAddress = (e, val) => {
    if (val == null) {
      setFieldValue('addressLine', '');
      setFieldValue('postalCode', '');
      setFieldValue('city', '');
      setFieldValue('country', null);
      return;
    }
    const { addressLine, postalCode, city, country } = val;
    setFieldValue('addressLine', addressLine ?? '');
    setFieldValue('postalCode', postalCode ?? '');
    setFieldValue('city', city ?? '');
    setFieldValue('country', country ?? null);
  };

  const getPossibleSubordinateValues = () => {
    const subordinates = currentCaseRelations.filter((rel) =>
      currentCaseRelations.some(
        (other) => other.id === rel.parentRelationId || other.id === rel.subordinate,
      ),
    );
    return otherCaseRelations
      .filter((rel) => !subordinates.some((sub) => sub.id === rel.id))
      .map((rel) => ({ label: rel.displayName || '', value: rel.id }));
  };

  const filteredBuildingRelations = useMemo(() => {
    const query = search.toLowerCase();

    return buildingRelations.filter((relation) => {
      const isExcluded = currentCaseRelations.some(
        (caseRelation) =>
          (relation.contactId !== null && caseRelation.contactId === relation.contactId) ||
          (relation.companyId !== null && caseRelation.companyId === relation.companyId),
      );

      if (isExcluded) {
        return false;
      }

      if (
        relation.units &&
        relation.units.some(
          (unit) =>
            unit.info?.toLowerCase().includes(query) ||
            unit.registrationNumber.toLowerCase().includes(query),
        )
      ) {
        return true;
      }

      return relation.displayName.toLowerCase().includes(query);
    });
  }, [buildingRelations, currentCaseRelations, search]);

  return (
    <Stack spacing={1} className="mr-4 mt-3">
      {!editMode && buildingId && (
        <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'flex-end' }}>
          <Badge
            badgeContent={filteredBuildingRelations?.length || null}
            color="primary"
            variant="solid"
          >
            <Checkbox
              checked={buildingContactsToggle.value}
              disabled={!filteredBuildingRelations?.length}
              label={t('suggestedContactsPerBuilding')}
              variant="outlined"
              onChange={() => {
                buildingContactsToggle.inverse();
                reset();
              }}
              sx={{ '--Checkbox-size': '18px' }}
            />
          </Badge>
        </Box>
      )}

      {!editMode && (
        <Box>
          {!buildingContactsToggle.value ? (
            <FormikCrmSelectorJoy
              name="crmSelector"
              label={t('clickHereToAddInvolvedParty')}
              callback={checkCrmSelection}
              excludeIds={relationIdsToExclude}
              includeTeams
              isRequired
            />
          ) : (
            !!buildingRelations.length && (
              <BuildingContactsSuggestions
                callback={(relation) => {
                  checkCrmSelection({
                    crmType: relation.contactId ? CrmOptionType.Contact : CrmOptionType.Company,
                    label: relation.displayName,
                    type: relation.contactId ? CrmOptionType.Contact : CrmOptionType.Company,
                    value: relation.contactId ?? relation.companyId,
                  });
                }}
                debounceSearch={debounceSearch}
                filteredBuildingRelations={filteredBuildingRelations}
                valueName="contactId"
              />
            )
          )}
        </Box>
      )}

      {!minified && (
        <Stack spacing={1}>
          {otherCaseRelations?.length > 0 && (
            <FormikAutocomplete
              id="subordinate"
              name="subordinate"
              options={getPossibleSubordinateValues()}
              label={t('common:subordinateTo')}
            />
          )}

          {selectedCrmInfo?.type === CrmOptionType.Company && <FormikVatInput name="vatnumber" />}

          <Grid container spacing={1}>
            <Grid xs={6}>
              <FormikCheckbox name="isRequestor" label={t('common:isRequestor')} />
            </Grid>
            {askForClient && (
              <Grid xs={6}>
                <FormikCheckbox name="isClient" label={t('common:isClient')} />
              </Grid>
            )}
          </Grid>

          {selectedCrmInfo && (
            <FormikEnumSelector name="language" label={t('common:language')} options={languages} />
          )}

          {selectedCrmInfo?.type !== CrmOptionType.Contact ? (
            <FormikTextField name="companyName" label={t('common:companyName')} required />
          ) : (
            <Stack spacing={0}>
              <FormikEnumSelector
                required
                name="gender"
                label={t('common:gender')}
                options={genders}
              />
              <Grid container spacing={2} sx={{ py: { md: 1, xs: 1 } }}>
                <Grid xs={6}>
                  <FormikTextField
                    name="firstName"
                    label={t('common:firstName')}
                    sx={{ pr: { md: 1, xs: 1 } }}
                  />
                </Grid>
                <Grid xs={6}>
                  <FormikTextField
                    name="lastName"
                    label={t('common:lastName')}
                    sx={{ pr: { md: 1, xs: 1 } }}
                    required
                  />
                </Grid>
              </Grid>
            </Stack>
          )}

          <FormikIbansInput />

          <FormikMultiPhoneNumbersInput />

          <FormikEmailInput name="emailAddresses" label={t('emailAddresses')} />

          <Grid container spacing={0}>
            <Grid xs={6} sx={{ pr: { md: 1, xs: 1 } }}>
              <FormikEnumSelector name="role" label={t('common:role')} options={caseRoles} />
            </Grid>
            <Grid xs={6} sx={{ pr: { md: 1, xs: 1 } }}>
              <FormikEnumSelector
                name="involvementType"
                label={t('common:involvementType')}
                options={caseInvolvementTypes}
              />
            </Grid>
          </Grid>

          <Stack spacing={1}>
            {!editMode &&
              selectedCrmInfo?.type === CrmOptionType.Company &&
              companyAddresses?.length > 1 && (
                <Autocomplete
                  placeholder={t('common:selectExistingAddress')}
                  options={companyAddresses}
                  onChange={prefillAddress}
                  getOptionLabel={(opt) =>
                    getAddressString(opt.addressLine, opt.city, opt.postalCode, opt.country)
                  }
                />
              )}
            <FormikAddressForm key={addressFormKey} />
          </Stack>
        </Stack>
      )}

      {editMode && <FormikCheckbox name="pushCrmChanges" label={t('common:pushCrmChanges')} />}
    </Stack>
  );
};

AddCaseRelationFormBody.propTypes = propTypes;

export default AddCaseRelationFormBody;
